import { useState } from 'react';
import { AlertCircle, Check } from 'lucide-react';
import type { IssueField, IssueForm } from '../../domain/types';
import type { TranslationKey } from '../../i18n';
import { MarkdownPreview } from './MarkdownPreview';
import styles from './Preview.module.css';

interface IssuePreviewProps {
  form: IssueForm;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
}

function FieldLabel({ field }: { field: Exclude<IssueField, { type: 'markdown' }> }) {
  const required =
    field.type === 'checkboxes'
      ? field.attributes.options.some((option) => option.required)
      : field.validations.required;
  return (
    <label className={styles.fieldLabel} htmlFor={`preview-${field.uid}`}>
      {field.attributes.label || 'Untitled field'}
      {required ? <span className={styles.required}> *</span> : null}
    </label>
  );
}

export function IssuePreview({ form, t }: IssuePreviewProps) {
  const [values, setValues] = useState<Record<string, string | string[] | boolean[]>>({});

  const setValue = (uid: string, value: string | string[] | boolean[]) => {
    setValues((current) => ({ ...current, [uid]: value }));
  };

  return (
    <main className={styles.canvas} aria-label={t('formPreview')} data-testid="preview-panel">
      <div className={styles.previewShell}>
        <header className={styles.issueHeader}>
          <div className={styles.previewEyebrow}>
            <AlertCircle size={14} strokeWidth={1.8} aria-hidden="true" />
            {t('formPreview')}
          </div>
          <h1>{form.name || 'Untitled issue form'}</h1>
          <p>{form.description || 'No description provided.'}</p>
          {form.labels.length > 0 ? (
            <div className={styles.labelRow} aria-label={t('labels')}>
              {form.labels.map((label) => (
                <span className={styles.issueLabel} key={label}>
                  {label}
                </span>
              ))}
            </div>
          ) : null}
        </header>

        <form className={styles.form} onSubmit={(event) => event.preventDefault()}>
          {form.body.map((field) => {
            if (field.type === 'markdown') {
              return (
                <section className={styles.markdownField} key={field.uid}>
                  <MarkdownPreview source={field.attributes.value} />
                </section>
              );
            }

            const description = field.attributes.description;
            if (field.type === 'input') {
              return (
                <section className={styles.formField} key={field.uid}>
                  <FieldLabel field={field} />
                  {description ? <p className={styles.help}>{description}</p> : null}
                  <input
                    id={`preview-${field.uid}`}
                    value={(values[field.uid] as string | undefined) ?? field.attributes.value}
                    placeholder={field.attributes.placeholder}
                    required={field.validations.required}
                    onChange={(event) => setValue(field.uid, event.target.value)}
                  />
                </section>
              );
            }

            if (field.type === 'textarea') {
              return (
                <section className={styles.formField} key={field.uid}>
                  <FieldLabel field={field} />
                  {description ? <p className={styles.help}>{description}</p> : null}
                  <textarea
                    id={`preview-${field.uid}`}
                    className={field.attributes.render ? styles.codeInput : undefined}
                    value={(values[field.uid] as string | undefined) ?? field.attributes.value}
                    placeholder={field.attributes.placeholder}
                    required={field.validations.required}
                    rows={5}
                    onChange={(event) => setValue(field.uid, event.target.value)}
                  />
                  {field.attributes.render ? (
                    <span className={styles.renderHint}>{field.attributes.render}</span>
                  ) : null}
                </section>
              );
            }

            if (field.type === 'dropdown') {
              const selected = values[field.uid];
              return (
                <section className={styles.formField} key={field.uid}>
                  <FieldLabel field={field} />
                  {description ? <p className={styles.help}>{description}</p> : null}
                  <select
                    id={`preview-${field.uid}`}
                    multiple={field.attributes.multiple}
                    required={field.validations.required}
                    value={
                      (selected as string | string[] | undefined) ??
                      (field.attributes.multiple ? [] : '')
                    }
                    onChange={(event) => {
                      if (field.attributes.multiple) {
                        setValue(
                          field.uid,
                          Array.from(event.target.selectedOptions, (option) => option.value),
                        );
                      } else {
                        setValue(field.uid, event.target.value);
                      }
                    }}
                  >
                    {!field.attributes.multiple ? (
                      <option value="">{t('selectOption')}</option>
                    ) : null}
                    {field.attributes.options.map((option) => (
                      <option value={option} key={option}>
                        {option || 'Empty option'}
                      </option>
                    ))}
                  </select>
                </section>
              );
            }

            const checked = (values[field.uid] as boolean[] | undefined) ?? [];
            return (
              <fieldset className={styles.formField} key={field.uid}>
                <legend>
                  {field.attributes.label || 'Untitled field'}
                  {field.attributes.options.some((option) => option.required) ? (
                    <span className={styles.required}> *</span>
                  ) : null}
                </legend>
                {description ? <p className={styles.help}>{description}</p> : null}
                <div className={styles.checkboxList}>
                  {field.attributes.options.map((option, index) => (
                    <label className={styles.checkbox} key={option.uid}>
                      <input
                        type="checkbox"
                        checked={checked[index] ?? false}
                        required={option.required}
                        onChange={(event) => {
                          const next = [...checked];
                          next[index] = event.target.checked;
                          setValue(field.uid, next);
                        }}
                      />
                      {option.label || 'Empty option'}
                    </label>
                  ))}
                </div>
              </fieldset>
            );
          })}

          <button type="submit" className={styles.submitButton}>
            <Check size={16} strokeWidth={2} aria-hidden="true" />
            {t('submitIssue')}
          </button>
        </form>
      </div>
    </main>
  );
}
